/**
 * Consumidor atual: useInstallPrompt (PWA). Os de push, exit intent e roleta
 * foram removidos em 2026-10-20; os nomes de slot seguem aceitos.
 * Per UI-SPEC §Z-Index Stack rules 1-3 + D-32 — overlay mutual exclusion state machine.
 * Module-level singleton; pure module (no React imports). Consumed by:
 *   - useInstallPrompt (Plan 3.5) — calls requestSlot("pwa_install") before mounting InstallPrompt
 *
 * Mutual exclusion: only one of "push" | "exitIntent" | "pwa_install" | "spin" can hold the slot at a time.
 * Both consumers also subscribe() to react when the OTHER overlay opens (defer mount).
 *
 * T-2.3-06: Subscriber callbacks are wrapped in try/catch; a failing subscriber
 * does NOT prevent others from being notified (mirrors Plan 2.1 onConsentChange pattern).
 */
export type ActiveOverlay = "push" | "exitIntent" | "pwa_install" | "spin" | null;

let active: ActiveOverlay = null;
const subscribers = new Set<(a: ActiveOverlay) => void>();

function notify(): void {
  for (const cb of subscribers) {
    try {
      cb(active);
    } catch (err) {
      if (import.meta.env.DEV) console.warn("[conv-orchestrator] subscriber error:", err);
    }
  }
}

/**
 * Request to mount an overlay. Returns true if granted, false if blocked by another overlay.
 * Caller MUST call releaseSlot(name) when overlay unmounts (dismiss/accept/close).
 */
export function requestSlot(name: "push" | "exitIntent" | "pwa_install" | "spin"): boolean {
  if (active !== null && active !== name) return false; // another overlay holds the slot
  active = name;
  notify();
  return true;
}

export function releaseSlot(name: "push" | "exitIntent" | "pwa_install" | "spin"): void {
  if (active === name) {
    active = null;
    notify();
  }
}

export function getActive(): ActiveOverlay {
  return active;
}

/** Subscribe to active overlay changes. Returns unsubscribe function. */
export function subscribe(cb: (active: ActiveOverlay) => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}
